import axios, {
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from "axios";
import { AppConfig } from "../../AppConfig";
import { appActions } from "../../ducks/app";
import { RequestArgs } from "../api/base";
import {
  AccountDetailAPI,
  AccountEditAPI,
  AccountListAPI,
  AccountRegisterAPI,
  AccountResetPasswordAPI,
  AuthCheckActivationAPI,
  AuthCheckEmailAPI,
  AuthLoginAPI,
  AuthLogoutAPI,
  AuthPasskeyChallengeLoginAPI,
  AuthPasskeyChallengeRegisterAPI,
  AuthPasskeyLoginAPI,
  AuthPasskeyRegisterAPI,
  AuthSendActivationOtpAPI,
  AuthSendOtpAPI,
  AuthEmailOtpAPI,
  AuthEmailVerifyAPI,
  AuthUpdateCredentialsAPI,
  AuthVerifyAPI,
  CustomerDetailAPI,
  CustomerEditAPI,
  CustomerListAPI,
  CustomerRegisterAPI,
  FileDeleteAPI,
  FileResultAPI,
  FileTemplateAPI,
  FileUploadAPI,
  FileUserAPI,
  GroupDetailAPI,
  GroupEditAPI,
  GroupListAPI,
  GroupRegisterAPI,
  SystemListAPI,
} from "../api/requests";
import { AccountUpdatePasswordAPI } from "../api/requests/account/update_password";
import { AuthTokenLoginAPI } from "../api/requests/auth/token_login";
import { FileDownloadAPI } from "../api/requests/file/download";
import { CookieManager } from "../storage/CookieManager";
import { LocalStorageManager } from "../storage/LocalStorageManager";
import { APIError } from "../types";
import { ErrorMessageManager } from "./ErrorMessageManager";

const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === "true";
const API_BASE_URL = process.env.REACT_APP_API_BASE ?? "";

export const axiosInstance = axios.create({
  baseURL: USE_MOCK_API ? "" : API_BASE_URL,
  withCredentials: true,
  useMockApi: USE_MOCK_API,
  displayError: true,
});

// リクエスト前の処理
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig<RequestArgs>) => {
    const accessToken = LocalStorageManager.Instance.getObject<string>(
      AppConfig.Instance.LocalStorageKey["accessToken"],
    );

    config.headers["Authorization"] = accessToken
      ? `Bearer ${accessToken}`
      : "";
    config.headers["Content-Type"] = config.data?.hasFile
      ? "multipart/form-data"
      : "application/json";

    if (config.data && Object.hasOwnProperty.call(config.data, "hasFile")) {
      config.data = {
        ...config.data,
        hasFile: undefined,
      };
    }

    return config;
  },
);

export const requestSuccess = (config: AxiosResponse<any, any>) => {
  return { ...config, response: { data: config.data } };
};

export const requestFailure = (error: APIError, store: any) => {
  const showErrorAlert = (errorCode: string) => {
    const errorMessage: string =
      ErrorMessageManager.Instance.getErrorMessage(errorCode);
    store.dispatch(
      appActions.showModal({
        title: errorMessage,
        buttons: [
          {
            variant: "dark",
            text: "閉じる",
            onClick: () => store.dispatch(appActions.hideModal()),
          },
        ],
      }),
    );
  };
  // TODO: Update error handling
  if (!error || !error.config?.displayError) {
  } else if (error && error.code === "ERR_NETWORK") {
    showErrorAlert("network-error");
  } else if (
    error &&
    error.response?.status === HttpStatusCode.ServiceUnavailable
  ) {
    showErrorAlert("internal-server-error");
  } else if (error && error.response?.status === HttpStatusCode.Unauthorized) {
    const errorMessage: string = ErrorMessageManager.Instance.getErrorMessage(
      error.response?.data["error-code"]
        ? error.response?.data["error-code"]
        : "unauthorized",
    );
    store.dispatch(
      appActions.showModal({
        title: errorMessage,
        buttons: [
          {
            variant: "dark",
            text: "閉じる",
            onClick: () => {
              store.dispatch(appActions.hideModal());
              LocalStorageManager.Instance.removeObject(
                AppConfig.Instance.LocalStorageKey["accessToken"],
              );
              LocalStorageManager.Instance.removeObject(
                AppConfig.Instance.LocalStorageKey["loggedInUser"],
              );
              CookieManager.Instance.removeObject(
                AppConfig.Instance.CookieKey["accessToken"],
                process.env.REACT_APP_COMMON_DOMAIN,
              );
              window.location.href = "/";
            },
          },
        ],
      }),
    );
  } else if (error && error.response?.status === HttpStatusCode.Forbidden) {
    showErrorAlert("forbidden");
  } else {
    showErrorAlert(error.response?.data["error-code"] ?? "unknown-error");
  }
  return Promise.reject({
    ...error,
    status: error.response?.status ?? HttpStatusCode.InternalServerError,
  });
};

export const domainBaseURLOption = () => {
  if (process.env.REACT_APP_USE_MOCK_API === "false") {
    return {
      ...axiosInstance.options,
      baseURL:
        process.env.REACT_APP_FILE_UPLOAD === "true"
          ? process.env.REACT_APP_FILE_UPLOAD_API_BASE
          : process.env.REACT_APP_API_BASE,
    };
  }
  return { ...axiosInstance.options };
};

const checkEmailApi = new AuthCheckEmailAPI(axiosInstance);
const loginApi = new AuthLoginAPI(axiosInstance);
const loginWithPasskeyApi = new AuthPasskeyLoginAPI(axiosInstance);
const logoutApi = new AuthLogoutAPI(axiosInstance);
const resetPasswordApi = new AccountResetPasswordAPI(axiosInstance);
const updatePasswordApi = new AccountUpdatePasswordAPI(axiosInstance);
const challengePasskeyRegisterApi = new AuthPasskeyChallengeRegisterAPI(
  axiosInstance,
);
const challengePasskeyLoginApi = new AuthPasskeyChallengeLoginAPI(
  axiosInstance,
);
const registerPasskeyApi = new AuthPasskeyRegisterAPI(axiosInstance);
const sendOtpApi = new AuthSendOtpAPI(axiosInstance);
const sendEmailOtpApi = new AuthEmailOtpAPI(axiosInstance);
const sendActivationOtpApi = new AuthSendActivationOtpAPI(axiosInstance);
const updateCredentialsApi = new AuthUpdateCredentialsAPI(axiosInstance);
const checkActivationApi = new AuthCheckActivationAPI(axiosInstance);
const verifyApi = new AuthVerifyAPI(axiosInstance);
const emailVerifyApi = new AuthEmailVerifyAPI(axiosInstance);
const tokenLoginApi = new AuthTokenLoginAPI(axiosInstance);

const customerListApi = new CustomerListAPI(axiosInstance);
const customerRegisterApi = new CustomerRegisterAPI(axiosInstance);
const customerDetailApi = new CustomerDetailAPI(axiosInstance);
const customerEditApi = new CustomerEditAPI(axiosInstance);
const groupListApi = new GroupListAPI(axiosInstance);
const groupRegisterApi = new GroupRegisterAPI(axiosInstance);
const groupDetailApi = new GroupDetailAPI(axiosInstance);
const groupEditApi = new GroupEditAPI(axiosInstance);
const accountListApi = new AccountListAPI(axiosInstance);
const accountRegisterApi = new AccountRegisterAPI(axiosInstance);
const accountDetailApi = new AccountDetailAPI(axiosInstance);
const accountEditApi = new AccountEditAPI(axiosInstance);

const fileTemplateApi = new FileTemplateAPI(axiosInstance);
const fileUserApi = new FileUserAPI(axiosInstance);
const fileResultApi = new FileResultAPI(axiosInstance);
const fileUploadApi = new FileUploadAPI(axiosInstance);
const fileDownloadApi = new FileDownloadAPI(axiosInstance);
const fileDeleteApi = new FileDeleteAPI(axiosInstance);

const systemListApi = new SystemListAPI(axiosInstance);

export {
  accountDetailApi,
  accountEditApi,
  accountListApi,
  accountRegisterApi,
  challengePasskeyLoginApi,
  challengePasskeyRegisterApi,
  checkActivationApi,
  checkEmailApi,
  customerDetailApi,
  customerEditApi,
  customerListApi,
  customerRegisterApi,
  fileDeleteApi,
  fileDownloadApi,
  fileResultApi,
  fileTemplateApi,
  fileUploadApi,
  fileUserApi,
  groupDetailApi,
  groupEditApi,
  groupListApi,
  groupRegisterApi,
  loginApi,
  loginWithPasskeyApi,
  logoutApi,
  registerPasskeyApi,
  resetPasswordApi,
  sendActivationOtpApi,
  sendOtpApi,
  sendEmailOtpApi,
  systemListApi,
  tokenLoginApi,
  updateCredentialsApi,
  updatePasswordApi,
  verifyApi,
  emailVerifyApi,
};
